import React, { useContext, useState } from 'react';

import { Box, Button, Container, Stack } from '@mui/material';

import Axios from '../../../../Assets/Axios/Axios';

import ToastContext from '../../../../Context/ToastContext';

import { TypoPoppins, TypoSerif } from '../../../../Components/Typo';

import {
  createProfessional,
  createService,
  ProCreateServicePackage,
  RollbackProfessionalRegistration,
} from '../../../../Server/Mutation';

import { toCapitalize } from '../../../../Helpers';

import FireBase from '../../../../lib/fireBaseProfessional';

import { signOutFirebaseUser } from '../../../../lib/firebaseClients';

import { LOGOUT } from '../../../../lib/Token';

import { USER_TYPES } from '../../../../Constant/Enums';

import { useFormContext } from './FormContext';

const DEFAULT_ERROR = 'Something went wrong while trying to create an account for you';

// The BFF returns specific GraphQL errors (e.g. "Phone already exists" /
// "Email already exists") for duplicate contact details. Surface a clear,
// actionable message instead of a generic one so the partner knows to go back
// and change the number/email. Falls back to the raw message, then DEFAULT_ERROR.
function friendlyRegistrationError(errors) {
  const first = errors?.[0];
  const field = first?.extensions?.field;
  if (field === 'mobileNumber' || first?.message === 'Phone already exists') {
    return 'This mobile number is already registered. Please go back and use a different number.';
  }
  if (field === 'email' || first?.message === 'Email already exists') {
    return 'This email is already registered. Please go back and use a different email.';
  }
  return first?.message || DEFAULT_ERROR;
}

const Confirmation = () => {
  const { showToastMessage } = useContext(ToastContext);
  const {
    inputs,
    serviceInputs,
    servicePackages,
    nextStep,
    stepTo,
    registrationState,
    uploadedUrls,
  } = useFormContext();

  const [isContinueBtnDisabled, setIsContinueBtnDisabled] = useState(false);
  // Set when a submit fails (and the partial signup is rolled back). Disables
  // Continue and offers an "Edit Details" button back to the first step, where the
  // already-entered data is still pre-filled (it lives in the parent form state).
  const [registrationFailed, setRegistrationFailed] = useState(false);

  // Delete the just-created professional DB row (best effort). Must run BEFORE
  // cleanupFailedRegistration, while the Firebase user is still signed in, since
  // the BFF authorizes this by the caller's ID token. Frees the mobile/email so a
  // half-finished registration doesn't permanently block re-registration.
  const rollbackProfessionalRow = async () => {
    try {
      await Axios(RollbackProfessionalRegistration());
    } catch (error) {
      // best-effort — the orphan sweep will remove a stale REQUESTED row later
      console.error('[registration] professional row rollback failed', error);
    }
  };

  // Roll back the half-finished signup: delete the orphaned Firebase user (best
  // effort), clear any local session, and sign out of the professional app.
  const cleanupFailedRegistration = async () => {
    try {
      await FireBase.deleteProfessional();
    } catch {
      // best-effort — the abandonment guard in the parent will retry on unmount
    }
    LOGOUT();
    await signOutFirebaseUser(USER_TYPES.PROFESSIONAL);
    // The Firebase user is gone — re-entering from step 1 will create a fresh one.
    registrationState.fbUserCreated = false;
  };

  // Go back to the first step to fix the details and retry. The entered values are
  // preserved in the parent form state, so every field comes back pre-filled.
  const handleClickEdit = () => {
    setRegistrationFailed(false);
    stepTo(1);
  };

  const handleClickContinue = async () => {
    setIsContinueBtnDisabled(true);
    setRegistrationFailed(false);
    let professionalCreated = false;
    try {
      // Images were already uploaded to S3 at the Service Details step, so here
      // we only create the DB rows using the stored URLs. The Firebase user was
      // created (and signed in) at the Personal Details step, so the Axios layer
      // attaches its ID token to the calls below even though no session flag is
      // stored — see getFirebaseToken's registration fallback.
      const { errors, data } = await Axios(createProfessional({
          ...inputs,
          avatar: uploadedUrls.avatar,
          nicImage: uploadedUrls.nicImage,
          addressProof: uploadedUrls.addressProof,
          fbUid: inputs.fbUid,
        }));

      if (errors) {
        await cleanupFailedRegistration();
        showToastMessage(friendlyRegistrationError(errors));
        setRegistrationFailed(true);
        return;
      }

      professionalCreated = true;

      const serviceCreateResponse = await Axios(createService({
          ...serviceInputs,
          bannerImage: uploadedUrls.bannerImage,
          icon: uploadedUrls.icon,
          images: uploadedUrls.serviceImages,
          professionalId: data.createProfessional.id,
          facebookUrl: serviceInputs.facebookUrl || null,
          websiteUrl: serviceInputs.websiteUrl || null,
        }));

      if (serviceCreateResponse.errors) {
        // createProfessional succeeded but the service didn't — roll back the
        // professional row first (while still authenticated), then the Firebase user.
        await rollbackProfessionalRow();
        await cleanupFailedRegistration();
        showToastMessage(friendlyRegistrationError(serviceCreateResponse.errors));
        setRegistrationFailed(true);
        return;
      }

      // Create the service's packages. Any failure rolls the whole registration
      // back (the rollback cascades: packages → service → professional row).
      const serviceId = serviceCreateResponse.data.createService.id;
      for (const pkg of servicePackages) {
        const packageResponse = await Axios(
          ProCreateServicePackage({ ...pkg, serviceId, features: [], isActive: true })
        );
        if (packageResponse.errors) {
          await rollbackProfessionalRow();
          await cleanupFailedRegistration();
          showToastMessage(friendlyRegistrationError(packageResponse.errors));
          setRegistrationFailed(true);
          return;
        }
      }

      // Registration finished — mark complete so the abandonment guard in the
      // parent doesn't delete the now-valid Firebase user.
      registrationState.completed = true;

      // Newly-registered professionals are pending approval (status REQUESTED),
      // so don't keep them logged in — clear any session flag AND sign out of
      // the professional Firebase app, otherwise the Axios registration
      // fallback would keep attaching their token after registration.
      LOGOUT();
      await signOutFirebaseUser(USER_TYPES.PROFESSIONAL);
      nextStep();
    } catch (error) {
      // Unexpected (network/JS) failure — if the professional row was already
      // created, roll it back (while still authenticated) before signing out.
      if (professionalCreated) await rollbackProfessionalRow();
      await cleanupFailedRegistration();
      showToastMessage(DEFAULT_ERROR);
      setRegistrationFailed(true);
    } finally {
      setIsContinueBtnDisabled(false);
    }
  };

  return (
    <Container maxWidth='sm'>
      <Stack alignItems='center' display='flex' my={8} gap={5}>
        <TypoSerif variant='h4' textAlign='center' variantMapping={{ h4: 'h1' }}>
          Confirmation
        </TypoSerif>

        <Box display='grid' gridTemplateColumns='repeat(2,1fr)' gap={2}>
          <TypoPoppins>Full name</TypoPoppins>
          <TypoPoppins>: {`${inputs.firstName} ${inputs.lastName}`}</TypoPoppins>
          <TypoPoppins>Address</TypoPoppins>
          <TypoPoppins>: {inputs.address}</TypoPoppins>
          <TypoPoppins>Province</TypoPoppins>
          <TypoPoppins>: {inputs.province}</TypoPoppins>
          <TypoPoppins>Post Code</TypoPoppins>
          <TypoPoppins>: {inputs.postalCode}</TypoPoppins>
          <TypoPoppins>Job Title</TypoPoppins>
          <TypoPoppins>: {toCapitalize(serviceInputs.name)}</TypoPoppins>
          <TypoPoppins>Service location</TypoPoppins>
          <TypoPoppins>: {serviceInputs.location}</TypoPoppins>
          <TypoPoppins>Hotline Number</TypoPoppins>
          <TypoPoppins>: {serviceInputs.hotlineNumber}</TypoPoppins>
        </Box>

        {servicePackages.length > 0 && (
          <Box width={1}>
            <TypoPoppins fontWeight={600} mb={1}>
              Packages
            </TypoPoppins>
            {servicePackages.map((pkg, index) => (
              <TypoPoppins key={index}>
                • {toCapitalize(pkg.name)} — {pkg.price}
              </TypoPoppins>
            ))}
          </Box>
        )}

        {registrationFailed && (
          <TypoPoppins textAlign='center' color='error.main'>
            We couldn’t complete your registration. Edit your details and try again.
          </TypoPoppins>
        )}

        {registrationFailed ? (
          <Button sx={{ px: 8 }} color='blue' onClick={handleClickEdit}>
            Edit Details
          </Button>
        ) : (
          <Button
            sx={{ px: 8 }}
            color='blue'
            onClick={handleClickContinue}
            disabled={isContinueBtnDisabled}
          >
            Continue
          </Button>
        )}
      </Stack>
    </Container>
  );
};

export default Confirmation;
